import { Component, EventEmitter, Input, LOCALE_ID, Output, SimpleChanges, OnInit, OnChanges, inject } from "@angular/core";
import { Utilities } from "@tylertech/shared-module";
import { ICalendarDate, PropertyMetadata } from "@tylertech/tyler-api-types";
import moment from "moment";
import { AugmentedModelObject, RepositoryFormData } from "../../interfaces";
import { MetadataEventService, MetadataPropertyService } from "../../services";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

export interface MetadataCalendarToggleParameter
{
    date: Date;
}

export interface IOrderedCalendarDate extends ICalendarDate
{
    DisplayIndex: number;
    DateKey: string;
    DayTotal: number;
    Key: string;
    StartDate: Date;
}

@Component({
    selector: "metadata-calendar-month",
    templateUrl: "./metadataCalendarMonth.tmpl.html",
    standalone: false
})

export class MetadataCalendarMonthComponent implements OnInit, OnChanges
{
    private $metadataPropertyService = inject(MetadataPropertyService);
    private $metadataEventService = inject(MetadataEventService);
    locale = inject(LOCALE_ID);

    @Input() formRepository: RepositoryFormData;
    @Input() propertyMetadata: PropertyMetadata;
    @Input() metadataModel: AugmentedModelObject;
    @Input() model: ICalendarDate[];
    @Input() parent: AugmentedModelObject;
    @Input() viewOnly: boolean;
    @Input() isDrillIn: boolean;
    @Input() calDate: moment.Moment;
    @Input() startDate: Date;
    @Output() private readonly toggleDrillIn = new EventEmitter<MetadataCalendarToggleParameter>();

    public namesOfDays = [
        "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"
    ];

    public displayedWeeks: Array<Date[]> = [];
    private displayedDates: Date[] = [];
    private firstDayOfRange = "calendar-range--start";
    private lastDayOfRange = "calendar-range--end";
    private isInitialized: boolean;
    currentMonthDates: ICalendarDate[];
    singleMonth: boolean = false;

    constructor()
    {
        const $metadataEventService = this.$metadataEventService;

        const self = this;
        $metadataEventService.onRecalculateWorking.pipe(takeUntilDestroyed())
            .subscribe(args =>
            {
                if (!args.Recalculating)
                {
                    self.setCurrentMonthDates();
                    self.setOrderedEvents();
                }
            });
    }

    ngOnInit(): void
    {
        if (!this.calDate)
        {
            this.singleMonth = true;
            const calMetadata = this.propertyMetadata.CalendarMetadata;
            if (!this.startDate)
                this.startDate = calMetadata.StartDateProperty
                    ? new Date(this.metadataModel[calMetadata.StartDateProperty])
                    : new Date(calMetadata.StartDate);
            this.setCalDate();
        }
        this.setDisplayedDates(this.calDate);
        this.setCurrentMonthDates();
        this.isInitialized = true;
    }

    orderedEvents: Record<string, IOrderedCalendarDate[]>;
    setOrderedEvents(): void
    {
        this.orderedEvents = {};

        this.displayedDates.forEach(dt =>
        {
            const currentEvents = <IOrderedCalendarDate[]>this.currentMonthDates.filter(x => x["$stringDate"] === dt.toDateString()).sort((a, b) => this.doSort(a, b));
            this.orderedEvents[dt.toDateString()] = currentEvents;
        });
    }

    doSort(a: ICalendarDate, b: ICalendarDate): number
    {
        const countA = this.currentMonthDates.filter(x => x.DateKey === a.DateKey).length;
        const countB = this.currentMonthDates.filter(x => x.DateKey === b.DateKey).length;

        if (countA === countB)
            return a.DateKey.toLowerCase() < b.DateKey.toLowerCase() ? -1 : 1;
        return countA > countB ? -1 : 1;
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        if (!this.isInitialized)
            return;
        if (changes["model"])
            this.setCurrentMonthDates();
    }

    private setDisplayedDates(currentMoment: moment.Moment): void
    {
        this.displayedDates = [];
        const firstOfMonth = moment(currentMoment)
            .startOf("month")
            .day();
        const lastOfMonth = moment(currentMoment)
            .endOf("month")
            .day();
        const firstDayOfGrid = moment(currentMoment)
            .startOf("month")
            .subtract(firstOfMonth, "days");
        const lastDayOfGrid = moment(currentMoment)
            .endOf("month")
            .subtract(lastOfMonth, "days")
            .add(6, "days");
        const startCalendar = firstDayOfGrid.date();
        const endCalendar = startCalendar + lastDayOfGrid.diff(firstDayOfGrid, "days");

        for (let date = startCalendar; date <= endCalendar; date++)
        {
            const newDate = moment(firstDayOfGrid).date(date);
            if (this.displayedDates.length < 42)
                this.displayedDates.push(newDate.toDate());
        }

        this.setDisplayedWeeks();
    }

    setDisplayedWeeks(): void
    {
        const weeks = [];
        const dates = Utilities.deepCopy(this.displayedDates);
        while (dates.length > 0)
        {
            weeks.push(dates.splice(0, 7));
        }
        this.displayedWeeks = weeks;
    }

    setCurrentMonthDates(): void
    {
        if (!this.model)
        {
            this.currentMonthDates = [];
            this.orderedEvents = {};
            return;
        }

        this.currentMonthDates = Utilities.deepCopy(this.model.filter(x => this.displayedDates.find(dt => dt.toDateString() === new Date(x.Day).toDateString())));
        if (this.currentMonthDates)
        {
            this.currentMonthDates.forEach(x =>
            {
                const previousDay = this.model.filter(model =>
                    new Date(model.Day).toDateString() === moment(this.displayedDates[0]).add(-1, "d")
                        .toDate()
                        .toDateString()
                    && model.DateKey === x.DateKey
                )?.length > 0;

                if(previousDay)
                {
                    x["$previousDay"] = true;
                }
                x["$stringDate"] = new Date(x.Day).toDateString();

                this.setFirstLastInRange(x, "first");
                this.setFirstLastInRange(x, "last");
            });
            this.setMultipleDatesDictionary();
        }
        if (this.displayedDates?.length > 0 && this.currentMonthDates?.length > 0)
            this.setOrderedEvents();
    }

    setFirstLastInRange(calendarDate: ICalendarDate, firstLast: string): void
    {
        const offset: number = firstLast === "first" ? 1 : -1;
        const match = this.currentMonthDates.filter(
            x =>
            {
                const date = new Date(x.Day);
                date.setDate(date.getDate() + offset);
                if (calendarDate.ShowDetail)
                    return x.DateKey === calendarDate.DateKey && new Date(date).toDateString() === new Date(calendarDate.Day).toDateString() && calendarDate.Color === x.Color && calendarDate.DateDetailKey === x.DateDetailKey;
                else
                    return x.DateKey === calendarDate.DateKey && new Date(date).toDateString() === new Date(calendarDate.Day).toDateString() && calendarDate.Color === x.Color;
            }
        ).length === 0;
        if (match)
            calendarDate[`$${firstLast}InRange`] = true;
    }

    multiple: Record<string, boolean> = {};

    setMultipleDatesDictionary(): void
    {
        const self = this;
        this.currentMonthDates.forEach(x =>
        {
            const date = x["$stringDate"];
            self.multiple[date] = !Utilities.IsNullOrUndefined(self.multiple[date]);
        });
    }

    showDay(day: Date): boolean
    {
        if (!this.displayedDates)
            return false;
        return day.getMonth() === this.displayedDates[15].getMonth();
    }

    showTooltip(day): boolean
    {
        return !Utilities.IsNullOrUndefinedOrEmpty(this.tooltip(day));
    }

    tooltip(day: Date): string
    {
        if (!this.showDay(day))
            return null;
        if (!this.orderedEvents)
            return null;
        const events = this.orderedEvents[day.toDateString()];
        if (Utilities.IsNullOrUndefinedOrEmpty(events))
            return null;
        return this.multiple[day["$stringDate"]] ? "Multiple events" : events[0].DateKey;
    }

    background(day: ICalendarDate): string
    {
        return this.multiple[day["$stringDate"]] ? "multiple" : day.Color;
    }

    dateRangeClass(day: Date): string
    {
        let ret = "";
        if (!this.showDay(day))
            return null;
        if (!this.orderedEvents)
            return null;
        const events = this.orderedEvents[day.toDateString()];
        if (Utilities.IsNullOrUndefinedOrEmpty(events))
        {
            ret += this.lastDayOfRange;
            ret += ` ${this.firstDayOfRange}`;
            return ret;
        }
        if (events[0].Selected)
        {
            if (this.firstLastClass(events[0], 1))
                ret += this.lastDayOfRange;
            if (this.firstLastClass(events[0], -1))
                ret += ` ${this.firstDayOfRange}`;
        }

        ret += ` event-color-${this.background(events[0])}`;

        switch (day.getDay())
        {
            case 0:
                ret += ` first-day-of-week`;
                break;
            case 6:
                ret += ` last-day-of-week`;
                break;
        }

        return ret;
    }

    firstLastClass(day: ICalendarDate, add: number): boolean
    {
        const dd = new Date(day.Day);
        const s = new Date(dd.setDate(dd.getDate() + add)).toDateString();
        return this.currentMonthDates.filter(
            x =>
            {
                return x["$stringDate"] === s && day.Color === x.Color;
            }
        ).length === 0;
    }

    drillIn(date?: any): void
    {
        this.toggleDrillIn.emit(<MetadataCalendarToggleParameter>{ date: date });
    }

    changeMonthText(val): string
    {
        return moment(this.startDate).add(val, "M")
            .format("MMMM YYYY");
    }

    changeMonth(val: moment.DurationInputArg1): void
    {
        this.startDate = this.calDate.add(val, "M").toDate();
        this.setCalDate();
        this.setDisplayedDates(this.calDate);
        this.setCurrentMonthDates();
    }

    setCalDate(): void
    {
        const month = new Date(this.startDate);
        month.setMonth(this.startDate.getMonth());
        this.calDate = moment(month);
    }

    onKeyDown(event: KeyboardEvent, day: Date): void
    {
        if(event.key === "Enter" || event.key === " ")
        {
            this.onClick(day);
        }
    }

    onClick(date: Date): void
    {
        if (!this.orderedEvents)
            return;
        const events = this.orderedEvents[date.toDateString()];
        if (Utilities.IsNullOrUndefinedOrEmpty(events))
            return;
        if (this.multiple[new Date(date).toDateString()])
        {
            this.drillIn(date);
            return;
        }
        const self = this;
        const clickedEvent = this.metadataModel[this.propertyMetadata.Name].find(x => x.Id === events[0].Id);
        if (clickedEvent)
            clickedEvent.Clicked = true;

        let type = this.metadataModel.$$TypeName;
        if (!type && this.formRepository) type = this.formRepository.Type;
        this.$metadataPropertyService.recalculateProperties(
            type,
            this.propertyMetadata,
            this.metadataModel,
            this.parent,
            this.metadataModel[this.propertyMetadata.Name],
            undefined,
            true,
            true,
            this.formRepository
        ).then(_x => self.metadataModel[self.propertyMetadata.Name].forEach(e => e.Clicked = false));
    }
}
